import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createGlow, createSuccessBurst, createWinBurst, createAchievementBurst } from '../utils/vfx';
import './SignUp.css';

const SignUp = () => {
  const [guestLoading, setGuestLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    ageRange: '',
    financialGoal: '',
    learningMode: 'facts'
  });
  const [loading, setLoading] = useState(false);
  const { actions } = useUser();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const authService = (await import('../services/AuthService')).default;
      const response = await authService.register(formData);

      if (response.token) {
        // Update user context
        actions.setUser(response.user);
        actions.setOnboarded(true);
        
        soundEffects.playAchievement();
        const signupButton = document.querySelector('.signup-button');
        if (signupButton) {
          createAchievementBurst(signupButton);
        }
        setTimeout(() => {
          const container = document.querySelector('.signup-container');
          if (container) {
            createWinBurst(container);
          }
        }, 300);
        
        toast.success('Account created successfully!');
        setTimeout(() => navigate('/dashboard'), 800);
      }
    } catch (error) {
      soundEffects.playError();
      toast.error(error.response?.data?.error || error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ageRanges = [
    { value: '13-17', label: '13-17 years' },
    { value: '18-24', label: '18-24 years' },
    { value: '25-34', label: '25-34 years' },
    { value: '35+', label: '35+ years' }
  ];

  const handleContinueAsGuest = async (e) => {
    e.preventDefault();
    soundEffects.playClick();
    setGuestLoading(true);
    try {
      const authService = (await import('../services/AuthService')).default;
      const response = await authService.guestLogin({
        name: 'Guest User',
        ageRange: '18-24',
        financialGoal: 'investing',
        learningMode: 'facts'
      });
      if (response.token) {
        actions.setUser(response.user);
        actions.setOnboarded(true);
        authService.saveUserData({
          user: response.user,
          isOnboarded: true,
          preferences: { learningMode: 'facts', notifications: true, dailyFactTime: '08:00' },
          progress: { level: 1, xp: 0, streak: 0, badges: [], completedFacts: [], completedLessons: [] },
          portfolio: { balance: 10000, stocks: [], transactions: [] }
        });
        toast.success("You're in! Exploring as guest.");
        setTimeout(() => navigate('/dashboard'), 400);
      }
    } catch (error) {
      setGuestLoading(false);
      toast.error(error.response?.data?.error || 'Could not start guest session. Please try again.');
    }
  };

  const financialGoals = [
    { value: 'college', label: 'Save for College' },
    { value: 'investing', label: 'Start Investing' },
    { value: 'budgeting', label: 'Learn Budgeting' },
    { value: 'retirement', label: 'Plan for Retirement' },
    { value: 'emergency', label: 'Build Emergency Fund' }
  ];

  return (
    <div className="signup-container">
      {/* Back Button */}
      <button
        className="signup-back-button"
        onMouseEnter={() => soundEffects.playHover()}
        onClick={(e) => {
          soundEffects.playClick();
          createRipple(e, '#ffffff');
          navigate('/');
        }}
      >
        ← Back
      </button>
      <div className="signup-card">
        <div className="signup-header">
          <div className="signup-logo">
            <img src="/LogoGurt.jpg" alt="InvestNYou Logo" className="signup-logo-img" />
          </div>
          <h1 className="signup-title">Create Account</h1>
          <p className="signup-subtitle">Start your financial literacy journey today</p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Create a password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ageRange" className="form-label">
              Age Range
            </label>
            <select
              id="ageRange"
              name="ageRange"
              value={formData.ageRange}
              onChange={handleChange}
              required
              className="form-select"
            >
              <option value="">Select your age range</option>
              {ageRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="financialGoal" className="form-label">
              Primary Financial Goal
            </label>
            <select
              id="financialGoal"
              name="financialGoal"
              value={formData.financialGoal}
              onChange={handleChange}
              required
              className="form-select"
            >
              <option value="">Select your main goal</option>
              {financialGoals.map((goal) => (
                <option key={goal.value} value={goal.value}>
                  {goal.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="learningMode" className="form-label">
              Learning Preference
            </label>
            <select
              id="learningMode"
              name="learningMode"
              value={formData.learningMode}
              onChange={handleChange}
              className="form-select"
            >
              <option value="facts">Daily Facts & Tips</option>
              <option value="courses">Structured Courses</option>
            </select>
          </div>

          <button
            type="submit"
            onClick={(e) => {
              const isValid = formData.name && formData.email && formData.password && formData.ageRange && formData.financialGoal;
              if (!loading && isValid) {
                soundEffects.playClick();
                createRipple(e, '#10b981');
              } else {
                soundEffects.playError();
              }
            }}
            onMouseEnter={(e) => {
              const isValid = formData.name && formData.email && formData.password && formData.ageRange && formData.financialGoal;
              if (!loading && isValid) {
                soundEffects.playHover();
                createGlow(e.currentTarget, '#10b981', 300);
              }
            }}
            disabled={loading}
            className={`signup-button ${
              loading || !formData.name || !formData.email || !formData.password || !formData.ageRange || !formData.financialGoal
                ? 'secondary'
                : 'primary'
            }`}
          >
            {loading ? (
              <div className="signup-button-content">
                <div className="signup-spinner"></div>
                Creating Account...
              </div>
            ) : (
              'Create Account'
            )}
          </button>

          <div className="guest-button">
            <button
              type="button"
              onClick={handleContinueAsGuest}
              disabled={guestLoading}
              className="guest-button-link"
              style={{ background: 'none', border: '1px solid #d1d5db', cursor: guestLoading ? 'not-allowed' : 'pointer' }}
            >
              {guestLoading ? 'Entering...' : 'Continue as Guest'}
            </button>
          </div>
        </form>

        <div className="signup-footer">
          <p className="signup-footer-text">
            Already have an account?{' '}
            <Link 
              to="/login" 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e, '#3b82f6');
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="signup-link"
            >
              Sign in here
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default SignUp;
